import { useState } from "react";
import { useNavigate } from "react-router";
import { useDotLegalSnackbar, useStateSessionStorage } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useCustomListExport } from "../../../customLists/customListView/useCustomListExport";
import { useUrlProvider } from "../../../useUrlProvider";
import { useDotLegalPostGetQueryArray, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { FilterOperatorUi, ListFilter, ListFilterOperator, SortDirection } from "@common/api/filterTypes";
import { EntityType } from "@common/Common.types";
import { FilterOption, FilterOptionType, RiskFieldGrouping } from "@common/components/filterPopover/FilterPopover.types";
import { GetBooleanData, GetListData, GetNumberData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper";
import { TableFields } from "@common/components/tableDisplayFields/TableFields.types";
import { usePageView } from "@common/pageView/PageView.hooks";
import { FilterPage, SessionStorageSavedView } from "@common/pageView/PageView.types";
import { useSavedViewQuery } from "@common/components/savedViews/SavedViewsMenu.hooks";
import { SavedView } from "@common/components/savedViews/SavedViewsMenu.types";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types";
import {
    allOperationalEntityStatuses,
    allRiskOperationalColumns,
    backendColumnToRisk,
    defaultRiskFilter,
    entityTypeLabel,
    OperationalEntityStatus,
    operationalEntityStatusTranslationKey,
    RiskChip,
    riskColumnToBackend,
    RiskMitigationSummary,
    RiskOperationalColumn,
    RiskOperationalExportHeader,
    RiskOperationalItem,
    RiskOperationalSavedFilter,
    RiskOperationalSearchResult,
    RiskOperationalTableRequest,
} from "../RiskOperationalOverview.types";
import { useSelectableControlsForRisk } from "../../riskControls/RiskControls.hooks";
import {
    assessableEntityTypeOptions,
    assessableEntityTypes,
    controlStatusFilterOptions,
    frameworkControlFilterOptions,
    fromBoolFilter,
    riskLevelFilterOptions,
    toBoolFilter,
    yesNoFilterOptions,
} from "../../riskFilterUtils";
import { boolParam, useSeedFilterFromUrl } from "@common/hooks/useSeedFilterFromUrl";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { SelectableRiskScenario } from "../../riskAssessmentsOverview/addRiskScenarioDialog/AddRiskScenarioDialog.types";
import { getSelectableBusinessAreasUrl, useSelectableTagsQuery } from "@common/hooks/useSelectableItemQueries";
import { SelectableColoredItem } from "../../../processingActivity/ProcessingActivity.types";
import { useRiskAreasContext } from "@common/providers/riskAreasProvider/RiskAreasProvider";
import { isLevelOverAppetite } from "../../riskAssessmentsOverview/RiskAssessmentsOverview.utils";
import { RiskExposureMode } from "../../riskExposure/RiskExposure.types";

const defaultPageSize = 50;
const searchStorageKeyPrefix = "risk-operational-overview-search";
const searchUrl = "/risk-areas/operational/search";

const typeFilterId = "types";
const statusFilterId = "statuses";
const isAssessedFilterId = "isAssessed";
const entityFilterId = "risk-entity-ids";
const responsibleFilterId = "responsibleIds";
const businessAreaFilterId = "businessAreaIds";
const tagFilterId = "tagIds";
const scenarioFilterId = "scenarioIds";
const riskFilterId = "riskLevels";
const afterMitigationFilterId = "afterMitigationLevels";
const hasMitigationFilterId = "hasMitigation";
const overAppetiteFilterId = "overAppetite";
const riskAcceptanceFilterId = "hasRiskAcceptance";
const avoidanceFilterId = "hasAvoidance";
const hasControlsFilterId = "hasControls";
const overAppetiteWithoutControlsFilterId = "hasOverAppetiteRiskWithoutControls";
const overAppetiteWithoutTreatmentFilterId = "hasOverAppetiteRiskWithoutTreatment";
const linkedRiskOverAppetiteFilterId = "hasLinkedRiskOverAppetite";
const frameworkControlFilterId = "frameworkControlIds";
const controlStatusFilterId = "controlStatuses";
const estimatedLossFilterId = "estimatedLoss";
const estimatedCostFilterId = "estimatedCost";
const exposureAfterMitigationFilterId = "exposureAfterMitigation";
const showOnlyMineFilterId = "show-only-mine";

const entitySearchUrl = "/risk-areas/operational/entities";

function toListFilter<T>(values: Array<T>, operator: FilterOperatorUi | undefined): ListFilter<T> | undefined {
    if (values.length === 0) return undefined;
    return { value: values, filterOperator: operator === FilterOperatorUi.Negative ? ListFilterOperator.NotIn : ListFilterOperator.In };
}

function extractIds(filter: ListFilter<unknown> | undefined): Array<string> {
    return (filter?.value ?? []).map((value) => String(value));
}

function extractOperator(filter: ListFilter<unknown> | undefined): FilterOperatorUi {
    return filter?.filterOperator === ListFilterOperator.NotIn ? FilterOperatorUi.Negative : FilterOperatorUi.Positive;
}

function toIdNamePairs(items: Array<{ id: string; name: string }> | undefined): Array<IdNamePair> {
    return (items ?? []).map((x) => ({ id: x.id, name: x.name }));
}

// What the Mitigations cell shows: nothing, or a neutral count. Always a count chip — consistent with the
// acceptance/avoidance columns. The plan's sub-status is a per-scenario detail seen on drill-in, not here.
export type MitigationDisplay = { kind: "none" } | { kind: "count"; count: number };

export function getMitigationDisplay(mitigation: RiskMitigationSummary | null): MitigationDisplay {
    if (!mitigation || mitigation.count === 0) return { kind: "none" };
    return { kind: "count", count: mitigation.count };
}

function isRiskColumn(column: string): column is RiskOperationalColumn {
    return allRiskOperationalColumns.includes(column as RiskOperationalColumn);
}

export function useEntityAssessmentsTable(riskAreaId: string) {
    const { translateString, translateFrameworkControlStatusEnum } = useTranslation();
    const navigate = useNavigate();
    const snackbar = useDotLegalSnackbar();
    const { exportRiskOperationalInBackground } = useCustomListExport();
    const { getRiskAssessmentsOverviewUrl, getProcessingActivityOverviewUrl, getSystemOverviewUrl, getLegalEntityUrl } = useUrlProvider();
    const { riskAreas } = useRiskAreasContext();

    const searchStorageKey = `${searchStorageKeyPrefix}:${riskAreaId}`;
    const [searchText, setSearchText] = useStateSessionStorage<string>(searchStorageKey, "");

    const [entityToRemove, setEntityToRemove] = useState<RiskOperationalItem | null>(null);
    const [controlsDialogItem, setControlsDialogItem] = useState<RiskOperationalItem | null>(null);
    const [isAddEntitiesOpen, setIsAddEntitiesOpen] = useState(false);

    const savedViewQuery = useSavedViewQuery(FilterPage.RiskOperationalOverview, riskAreaId);
    const {
        currentViewId,
        getFilterData,
        getDisplayData,
        setFilterData,
        setDisplayData,
        clearFilterData,
        hasFilterChanged,
        hasDisplayChanged,
        isSavedView,
        savedView,
        setView,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = usePageView<RiskOperationalSavedFilter, Array<RiskOperationalColumn>>(FilterPage.RiskOperationalOverview, savedViewQuery.data, riskAreaId);

    const filterData = getFilterData<RiskOperationalSavedFilter>() ?? defaultRiskFilter;
    // The backend system-default view supplies the default columns once saved views resolve; until then the
    // table is in its loading state, so an empty fallback never renders.
    const visibleColumns = getDisplayData<Array<RiskOperationalColumn>>() ?? [];

    // The governance over-appetite tile carries overAppetite and its gap line overAppetiteWithoutTreatment, the
    // coverage tile's red band carries overAppetiteWithoutControls, the linked-risk tile carries
    // overAppetite=false + linkedRiskOverAppetite, and the Assessed tile's gap carries notAssessed.
    useSeedFilterFromUrl(
        !savedViewQuery.isLoading,
        ["overAppetite", "overAppetiteWithoutControls", "overAppetiteWithoutTreatment", "linkedRiskOverAppetite", "notAssessed"],
        (params) => {
            const overAppetiteParam = boolParam(params, "overAppetite");
            const overAppetiteWithoutControlsParam = boolParam(params, "overAppetiteWithoutControls");
            const overAppetiteWithoutTreatmentParam = boolParam(params, "overAppetiteWithoutTreatment");
            const linkedRiskOverAppetiteParam = boolParam(params, "linkedRiskOverAppetite");
            const notAssessedParam = boolParam(params, "notAssessed");

            // The governance figures count every entity curated onto the area whatever its own lifecycle status, so
            // the landing filter must drop the default's Active+Draft status selection: an archived entity behind a
            // tile's number has to appear in the rows that number lands on, or the figure and the table disagree.
            const seeded: RiskOperationalSavedFilter = { ...defaultRiskFilter };
            delete seeded.statuses;
            if (overAppetiteParam !== undefined) seeded.overAppetite = overAppetiteParam;
            if (overAppetiteWithoutControlsParam !== undefined) seeded.hasOverAppetiteRiskWithoutControls = overAppetiteWithoutControlsParam;
            if (overAppetiteWithoutTreatmentParam !== undefined) seeded.hasOverAppetiteRiskWithoutTreatment = overAppetiteWithoutTreatmentParam;
            if (linkedRiskOverAppetiteParam !== undefined) seeded.hasLinkedRiskOverAppetite = linkedRiskOverAppetiteParam;
            // The param states the gap, the filter states the property: "not assessed" seeds Assessed = Nej.
            if (notAssessedParam !== undefined) seeded.isAssessed = !notAssessedParam;
            setFilterData(seeded);
        }
    );

    const controlsQuery = useSelectableControlsForRisk();

    const responsibleQuery = useResponsibleQuery();
    const businessAreaQuery = useDotLegalQuery<Array<SelectableColoredItem>>(getSelectableBusinessAreasUrl());
    const tagQuery = useSelectableTagsQuery();

    // Scenario-filter options: every scenario applicable to the area across all assessable entity types. Reuses
    // the same endpoint the bulk-assess/mitigate dialogs use; may include scenarios not yet assessed anywhere.
    const allEntityTypesQueryString = assessableEntityTypes.map((type) => `entityTypes=${type}`).join("&");
    const scenariosQuery = useDotLegalQuery<Array<SelectableRiskScenario>>(`/risk-assessments/${riskAreaId}/applicable-scenarios?${allEntityTypesQueryString}`);

    const riskAreaName = riskAreas.find((x) => x.id === riskAreaId)?.name;

    const request: RiskOperationalTableRequest = {
        displayFields: {
            selectedFields: visibleColumns.map((column, index) => ({ column: riskColumnToBackend[column], displayOrder: index })),
        },
        filter: {
            riskAreaId,
            showOnlyMine: filterData.showOnlyMine,
            types: filterData.types,
            statuses: filterData.statuses,
            isAssessed: filterData.isAssessed,
            entityIds: filterData.entityIds,
            responsibleIds: filterData.responsibleIds,
            businessAreaIds: filterData.businessAreaIds,
            tagIds: filterData.tagIds,
            scenarioIds: filterData.scenarioIds,
            riskLevels: filterData.riskLevels,
            afterMitigationLevels: filterData.afterMitigationLevels,
            hasMitigation: filterData.hasMitigation,
            overAppetite: filterData.overAppetite,
            hasRiskAcceptance: filterData.hasRiskAcceptance,
            hasAvoidance: filterData.hasAvoidance,
            hasControls: filterData.hasControls,
            hasOverAppetiteRiskWithoutControls: filterData.hasOverAppetiteRiskWithoutControls,
            hasOverAppetiteRiskWithoutTreatment: filterData.hasOverAppetiteRiskWithoutTreatment,
            hasLinkedRiskOverAppetite: filterData.hasLinkedRiskOverAppetite,
            frameworkControlIds: filterData.frameworkControlIds,
            controlStatuses: filterData.controlStatuses,
            estimatedLossFrom: filterData.estimatedLossFrom,
            estimatedLossTo: filterData.estimatedLossTo,
            estimatedCostFrom: filterData.estimatedCostFrom,
            estimatedCostTo: filterData.estimatedCostTo,
            exposureAfterMitigationFrom: filterData.exposureAfterMitigationFrom,
            exposureAfterMitigationTo: filterData.exposureAfterMitigationTo,
            searchText: searchText.trim() || undefined,
            pageNumber: filterData.pageNumber,
            pageSize: defaultPageSize,
            sortByColumn: filterData.sortByColumn,
            sortDirection: filterData.sortDirection,
        },
    };

    const { data, isLoading, refetch } = useDotLegalPostGetQueryArray<RiskOperationalSearchResult>([searchUrl, request], searchUrl, request, {
        enabled: !savedViewQuery.isLoading,
    });

    const exposureMode = data?.exposureMode ?? RiskExposureMode.None;
    const exposureEnabled = exposureMode !== RiskExposureMode.None;

    const typeOptions: Array<IdNamePair> = assessableEntityTypeOptions(translateString);
    const statusOptions: Array<IdNamePair> = allOperationalEntityStatuses.map((status) => ({
        id: status.toString(),
        name: translateString(operationalEntityStatusTranslationKey[status]),
    }));
    const responsibleOptions = toIdNamePairs(responsibleQuery.userData);
    const businessAreaOptions = toIdNamePairs(businessAreaQuery.data);
    const tagOptions = toIdNamePairs(tagQuery.data);
    const scenarioOptions = toIdNamePairs(scenariosQuery.data);
    // The area's risk level labels, shared by the Risk and After mitigation filters so both offer the same bands.
    const riskLevelOptions = riskLevelFilterOptions(data?.riskLevels);
    const yesNoOptions = yesNoFilterOptions(translateString);
    const frameworkControlOptions = frameworkControlFilterOptions(controlsQuery.data);
    const controlStatusOptions = controlStatusFilterOptions(translateFrameworkControlStatusEnum);

    const filterOptions: Array<FilterOption> = [
        {
            id: showOnlyMineFilterId,
            label: translateString("show"),
            type: FilterOptionType.Boolean,
            value: filterData.showOnlyMine ?? false,
            fixedData: [
                { id: "0", name: translateString("all") },
                { id: "1", name: translateString("my") },
            ],
        },
        {
            id: typeFilterId,
            label: translateString("type"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.types),
            fixedData: typeOptions,
            filterOperator: extractOperator(filterData.types),
        },
        {
            id: statusFilterId,
            label: translateString("entityStatus"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.statuses),
            fixedData: statusOptions,
            filterOperator: extractOperator(filterData.statuses),
        },
        {
            id: entityFilterId,
            label: translateString("entity"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.entityIds),
            searchUrl: entitySearchUrl,
            filterOperator: extractOperator(filterData.entityIds),
        },
        {
            id: responsibleFilterId,
            label: translateString("responsible"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.responsibleIds),
            fixedData: responsibleOptions,
            filterOperator: extractOperator(filterData.responsibleIds),
        },
        {
            id: businessAreaFilterId,
            label: translateString("businessArea"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.businessAreaIds),
            fixedData: businessAreaOptions,
            filterOperator: extractOperator(filterData.businessAreaIds),
        },
        {
            id: tagFilterId,
            label: translateString("tags"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.tagIds),
            fixedData: tagOptions,
            filterOperator: extractOperator(filterData.tagIds),
        },
        {
            id: isAssessedFilterId,
            label: translateString("assessed"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.isAssessed),
            fixedData: yesNoOptions,
        },
        {
            id: scenarioFilterId,
            label: translateString("scenario"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.scenarioIds),
            fixedData: scenarioOptions,
            filterOperator: extractOperator(filterData.scenarioIds),
        },
        {
            id: riskFilterId,
            label: translateString("risk"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.riskLevels),
            fixedData: riskLevelOptions,
            filterOperator: extractOperator(filterData.riskLevels),
        },
        {
            id: afterMitigationFilterId,
            label: translateString("afterMitigation"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.afterMitigationLevels),
            fixedData: riskLevelOptions,
            filterOperator: extractOperator(filterData.afterMitigationLevels),
        },
        {
            id: overAppetiteFilterId,
            label: translateString("riskGovernanceOverAppetite"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.overAppetite),
            fixedData: yesNoOptions,
        },
        // The governance linked-risk tile: a directly linked entity over appetite (combined with Over appetite =
        // Nej it is that tile's exact landing set).
        {
            id: linkedRiskOverAppetiteFilterId,
            label: translateString("linkedRiskOverAppetite"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.hasLinkedRiskOverAppetite),
            fixedData: yesNoOptions,
        },
        {
            id: hasMitigationFilterId,
            label: translateString("mitigations"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.hasMitigation),
            fixedData: yesNoOptions,
        },
        {
            id: riskAcceptanceFilterId,
            label: translateString("acceptances"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.hasRiskAcceptance),
            fixedData: yesNoOptions,
        },
        {
            id: avoidanceFilterId,
            label: translateString("avoidances"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.hasAvoidance),
            fixedData: yesNoOptions,
        },
        {
            id: hasControlsFilterId,
            label: translateString("hasLinkedControls"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.hasControls),
            fixedData: yesNoOptions,
        },
        {
            id: frameworkControlFilterId,
            label: translateString("controls"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.frameworkControlIds),
            fixedData: frameworkControlOptions,
            filterOperator: extractOperator(filterData.frameworkControlIds),
        },
        {
            id: controlStatusFilterId,
            label: translateString("controlStatus"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.controlStatuses),
            fixedData: controlStatusOptions,
            filterOperator: extractOperator(filterData.controlStatuses),
        },
        // The governance coverage tile's red band: a live over-appetite risk with no control on the risk itself.
        {
            id: overAppetiteWithoutControlsFilterId,
            label: translateString("riskOverAppetiteWithoutControls"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.hasOverAppetiteRiskWithoutControls),
            fixedData: yesNoOptions,
        },
        // The governance over-appetite tile's gap line: a live over-appetite risk with no treatment decision at all.
        {
            id: overAppetiteWithoutTreatmentFilterId,
            label: translateString("riskOverAppetiteWithoutTreatment"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.hasOverAppetiteRiskWithoutTreatment),
            fixedData: yesNoOptions,
        },
        ...(exposureEnabled
            ? [
                  {
                      id: estimatedLossFilterId,
                      label: translateString("riskExposure"),
                      type: FilterOptionType.Number,
                      value: { from: filterData.estimatedLossFrom, to: filterData.estimatedLossTo },
                  },
                  {
                      id: estimatedCostFilterId,
                      label: translateString("estimatedCost"),
                      type: FilterOptionType.Number,
                      value: { from: filterData.estimatedCostFrom, to: filterData.estimatedCostTo },
                  },
                  {
                      id: exposureAfterMitigationFilterId,
                      label: translateString("exposureAfterMitigationLabel"),
                      type: FilterOptionType.Number,
                      value: { from: filterData.exposureAfterMitigationFrom, to: filterData.exposureAfterMitigationTo },
                  },
              ]
            : []),
    ];

    const onFilterChange = (updatedOptions: Array<FilterOption>) => {
        const next: RiskOperationalSavedFilter = { ...filterData, pageNumber: 1 };
        updatedOptions.forEach((option) => {
            if (option.id === showOnlyMineFilterId) {
                next.showOnlyMine = GetBooleanData(option.value) === "1";
                return;
            }

            // Numeric range filters carry a {from,to} object, not a list, so parse them before the list path.
            if (option.type === FilterOptionType.Number) {
                const { from, to } = GetNumberData(option.value);
                switch (option.id) {
                    case estimatedLossFilterId:
                        next.estimatedLossFrom = from;
                        next.estimatedLossTo = to;
                        break;
                    case estimatedCostFilterId:
                        next.estimatedCostFrom = from;
                        next.estimatedCostTo = to;
                        break;
                    case exposureAfterMitigationFilterId:
                        next.exposureAfterMitigationFrom = from;
                        next.exposureAfterMitigationTo = to;
                        break;
                }
                return;
            }

            const values = GetListData(option.value);
            switch (option.id) {
                case typeFilterId:
                    next.types = toListFilter(
                        values.map((x) => Number(x) as EntityType),
                        option.filterOperator
                    );
                    break;
                case statusFilterId:
                    next.statuses = toListFilter(
                        values.map((x) => Number(x) as OperationalEntityStatus),
                        option.filterOperator
                    );
                    break;
                case isAssessedFilterId:
                    next.isAssessed = toBoolFilter(values);
                    break;
                case entityFilterId:
                    next.entityIds = toListFilter(values, option.filterOperator);
                    break;
                case responsibleFilterId:
                    next.responsibleIds = toListFilter(values, option.filterOperator);
                    break;
                case businessAreaFilterId:
                    next.businessAreaIds = toListFilter(values, option.filterOperator);
                    break;
                case tagFilterId:
                    next.tagIds = toListFilter(values, option.filterOperator);
                    break;
                case scenarioFilterId:
                    next.scenarioIds = toListFilter(values, option.filterOperator);
                    break;
                case riskFilterId:
                    next.riskLevels = toListFilter(values.map(Number), option.filterOperator);
                    break;
                case afterMitigationFilterId:
                    next.afterMitigationLevels = toListFilter(values.map(Number), option.filterOperator);
                    break;
                case hasMitigationFilterId:
                    next.hasMitigation = toBoolFilter(values);
                    break;
                case overAppetiteFilterId:
                    next.overAppetite = toBoolFilter(values);
                    break;
                case riskAcceptanceFilterId:
                    next.hasRiskAcceptance = toBoolFilter(values);
                    break;
                case avoidanceFilterId:
                    next.hasAvoidance = toBoolFilter(values);
                    break;
                case hasControlsFilterId:
                    next.hasControls = toBoolFilter(values);
                    break;
                case overAppetiteWithoutControlsFilterId:
                    next.hasOverAppetiteRiskWithoutControls = toBoolFilter(values);
                    break;
                case overAppetiteWithoutTreatmentFilterId:
                    next.hasOverAppetiteRiskWithoutTreatment = toBoolFilter(values);
                    break;
                case linkedRiskOverAppetiteFilterId:
                    next.hasLinkedRiskOverAppetite = toBoolFilter(values);
                    break;
                case frameworkControlFilterId:
                    next.frameworkControlIds = toListFilter(values, option.filterOperator);
                    break;
                case controlStatusFilterId:
                    next.controlStatuses = toListFilter(values.map(Number), option.filterOperator);
                    break;
            }
        });

        // Drop cleared (undefined) filters so the persisted shape matches a saved view's JSON round-tripped
        // filter data. Otherwise change detection compares against phantom keys and the reset button never clears.
        const cleaned = next as unknown as Record<string, unknown>;
        Object.keys(cleaned).forEach((key) => {
            if (cleaned[key] === undefined) delete cleaned[key];
        });

        setFilterData(next);
    };

    const onSearchTextChange = (value: string) => {
        setSearchText(value ?? "");
        setFilterData({ ...filterData, pageNumber: 1 });
    };

    const onSortChange = (column: RiskOperationalColumn, direction: SortDirection) => {
        setFilterData({ ...filterData, sortByColumn: riskColumnToBackend[column], sortDirection: direction, pageNumber: 1 });
    };

    const onPageChange = (page: number) => {
        setFilterData({ ...filterData, pageNumber: page });
    };

    const onColumnsSave = (columns: Array<string>) => {
        setDisplayData(columns.filter(isRiskColumn));
    };

    const applyView = (viewId: string | undefined, filter: RiskOperationalSavedFilter, display: Array<RiskOperationalColumn>) => {
        const updatedView: SessionStorageSavedView = {
            page: FilterPage.RiskOperationalOverview,
            viewId,
            filterData: filter,
            displayData: display,
        };
        setView(updatedView);
    };

    const onViewSaved = (viewId: string) => {
        applyView(viewId, filterData, visibleColumns);
    };

    const onViewDeleted = (deletedViewId: string) => {
        if (deletedViewId !== currentViewId) return;

        const fallbackView = savedViewQuery.data?.find((x: SavedView) => x.isCustomerDefault || x.isSystemDefault);
        applyView(fallbackView?.id === deletedViewId ? undefined : fallbackView?.id, filterData, visibleColumns);
    };

    const onChangeView = (view: SavedView) => {
        if (view.id !== currentViewId) {
            applyView(view.id, view.filterData, view.displayData);
        }
    };

    const tableFields: Array<TableFields> = [
        { id: RiskOperationalColumn.Type, name: translateString("type"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.Name, name: translateString("entity"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.EntityStatus, name: translateString("entityStatus"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.BusinessArea, name: translateString("businessArea"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.Responsible, name: translateString("responsible"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.Tags, name: translateString("tags"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.Risk, name: translateString("risk"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.AfterMitigation, name: translateString("afterMitigation"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.LinkedRisk, name: translateString("linkedRisk"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.Mitigation, name: translateString("mitigations"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.RiskAcceptance, name: translateString("acceptances"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.Avoidance, name: translateString("avoidances"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.ControlCount, name: translateString("controlCount"), grouping: RiskFieldGrouping.Risk },
        { id: RiskOperationalColumn.ControlStatus, name: translateString("controlStatus"), grouping: RiskFieldGrouping.Risk },
        ...(exposureEnabled
            ? [
                  { id: RiskOperationalColumn.EstimatedLoss, name: translateString("riskExposure"), grouping: RiskFieldGrouping.Risk },
                  { id: RiskOperationalColumn.EstimatedCost, name: translateString("estimatedCost"), grouping: RiskFieldGrouping.Risk },
                  {
                      id: RiskOperationalColumn.ExposureAfterMitigation,
                      name: translateString("exposureAfterMitigationLabel"),
                      grouping: RiskFieldGrouping.Risk,
                  },
              ]
            : []),
    ];

    // Render only persisted columns that are still selectable. tableFields omits the exposure columns when
    // exposure is not configured, so this drops them from the system-default view instead of showing a blank.
    // While the data (and thus the exposure mode) is still loading, tableFields hasn't yet gained the exposure
    // columns, so keep the selected columns as-is until we know the configuration to avoid transiently dropping
    // a selected exposure column from the header.
    const renderedColumns = isLoading ? visibleColumns : visibleColumns.filter((column) => tableFields.some((field) => field.id === column));

    const onDownloadExcelReportClick = async () => {
        const headerByColumn = new Map(tableFields.map((field) => [field.id, field.name]));
        const headers: Array<RiskOperationalExportHeader> = renderedColumns.map((column) => ({
            header: headerByColumn.get(column) ?? "",
            column,
        }));

        await exportRiskOperationalInBackground(savedView!.name, headers, request);
        snackbar.show(translateString("riskOperationalDataExportInProgress"), "success", true);
    };

    const getTypeLabel = (type: EntityType) => entityTypeLabel(type, translateString);

    const getEntityStatusLabel = (status: OperationalEntityStatus | null) =>
        status === null ? "" : translateString(operationalEntityStatusTranslationKey[status]);

    const tagChipById = new Map<string, RiskChip>((tagQuery.data ?? []).map((x) => [x.id, { name: x.name, color: x.color }]));
    const businessAreaChipById = new Map<string, RiskChip>((businessAreaQuery.data ?? []).map((x) => [x.id, { name: x.name, color: x.color }]));
    const responsibleNamesById = new Map((responsibleQuery.userData ?? []).map((x) => [x.id, x.name]));

    const getTagChips = (tagIds: Array<string>) => tagIds.map((id) => tagChipById.get(id)).filter((chip): chip is RiskChip => !!chip);
    const getBusinessAreaChips = (businessAreaIds: Array<string>) =>
        businessAreaIds.map((id) => businessAreaChipById.get(id)).filter((chip): chip is RiskChip => !!chip);
    const getResponsibleName = (responsibleId: string | null) => (responsibleId ? (responsibleNamesById.get(responsibleId) ?? "") : "");

    // The area's current-config indicators colour every level chip in the table.
    const riskLevelIndicators = data?.riskLevelIndicators ?? [];

    const isOverAppetite = (level: number) => isLevelOverAppetite(level, data?.appetite ?? 0);

    const getItemUrl = (item: RiskOperationalItem) => getRiskAssessmentsOverviewUrl(riskAreaId, item.type, item.entityId);

    const navigateToAssessments = (item: RiskOperationalItem) => navigate(getItemUrl(item));

    const [entityGraphTarget, setEntityGraphTarget] = useState<{ type: EntityType; entityId: string } | null>(null);
    const openEntityGraph = (item: RiskOperationalItem) => setEntityGraphTarget({ type: item.type, entityId: item.entityId });
    const closeEntityGraph = () => setEntityGraphTarget(null);

    return {
        pinnedFilterIds,
        setPinnedFilterIds,
        riskAreaName,
        result: data,
        isLoading: isLoading || savedViewQuery.isLoading,
        pageNumber: filterData.pageNumber,
        pageSize: defaultPageSize,
        sortByColumn: backendColumnToRisk[filterData.sortByColumn] ?? RiskOperationalColumn.Name,
        sortDirection: filterData.sortDirection,
        onSortChange,
        onPageChange,
        filterOptions,
        onFilterChange,
        clearFilterData,
        searchText,
        onSearchTextChange,
        visibleColumns,
        renderedColumns,
        tableFields,
        hasDisplayChanged,
        hasFilterChanged,
        isSavedView,
        savedView,
        currentViewId,
        filterData,
        onColumnsSave,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        onDownloadExcelReportClick,
        getTypeLabel,
        getEntityStatusLabel,
        getTagChips,
        getBusinessAreaChips,
        getResponsibleName,
        riskLevelIndicators,
        isOverAppetite,
        getMitigationDisplay,
        navigateToAssessments,
        refetch,
        getItemUrl,
        entityToRemove,
        openRemoveEntity: (item: RiskOperationalItem) => setEntityToRemove(item),
        closeRemoveEntity: () => setEntityToRemove(null),
        // Distinguishes an empty table caused by an active search/filter/saved view from a risk area that
        // simply has no entities added yet, so the two cases can show different empty messages.
        isSearch: isSavedView || hasFilterChanged || searchText.trim().length > 0,
        entityGraphTarget,
        openEntityGraph,
        closeEntityGraph,
        controlsDialogItem,
        openControlsDialog: setControlsDialogItem,
        closeControlsDialog: () => setControlsDialogItem(null),
        isAddEntitiesOpen,
        openAddEntities: () => setIsAddEntitiesOpen(true),
        closeAddEntities: () => setIsAddEntitiesOpen(false),
    };
}
